import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs } from "@expert/shared-types";

import { type TimelineFeatures, useFeatures } from "@expert/features";
import { trackFullStoryEvent } from "@expert/monitoring";
import { useDebouncedValue } from "@mantine/hooks";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import { useCallback, useEffect, useState } from "react";

import { useTimelineStore } from "../state";
import { useRequestAutoCompleteSuggestions } from "./useRequestAutoCompleteSuggestions";

interface HandleAutoCompleteProps {
    input: string;
    logger: Logger;
    gaiaPayloadProps: GaiaPayloadConfigs;
    toolboxInput?: string;
    isSendDisabled: boolean;
    setInput: (input: string) => void;
    setPreviousInput: (input: string) => void;
    setSelectedSuggestion: (suggestion: string) => void;
}

const AUTO_COMPLETE_DELAY = 500;

export function useHandleAutoComplete({
    input,
    logger,
    toolboxInput,
    isSendDisabled,
    setInput,
    setPreviousInput,
    setSelectedSuggestion,
    gaiaPayloadProps,
}: HandleAutoCompleteProps) {
    const localLogger = logger.child({ module: "useHandleAutoComplete" });
    const { dispatcher } = useReactAnalytics();
    const { setNewSuggestions, currentSessionId } = useTimelineStore();
    const {
        features: { proactiveSuggestionInterval },
    } = useFeatures<TimelineFeatures>();

    const [didClickSuggestion, setDidClickSuggestion] = useState(false);
    const [shouldRequestSuggestion, setShouldRequestSuggestion] = useState(false);

    const [debouncedInput, cancel] = useDebouncedValue(input, AUTO_COMPLETE_DELAY);
    const { requestAutoCompleteSuggestions } = useRequestAutoCompleteSuggestions(logger, gaiaPayloadProps);

    const handleSuggestionClick = useCallback(
        (suggestion: string) => {
            void dispatcher.dispatchBusinessEvent("AutoComplete_SuggestionClicked", { suggestion });
            trackFullStoryEvent("AutoComplete_SuggestionClicked", { suggestion });
            setPreviousInput(input);
            setInput(suggestion);
            setSelectedSuggestion(suggestion);
            localLogger.info({ suggestion }, "AutoComplete | suggestion selected");
            setNewSuggestions(currentSessionId, []);
            setDidClickSuggestion(true);
        },
        [
            dispatcher,
            input,
            setInput,
            setNewSuggestions,
            setPreviousInput,
            setSelectedSuggestion,
            localLogger,
            currentSessionId,
        ],
    );

    /*
        This keeps track of when we need to request suggestions.
        This should only happen once after the user stops typing
        (i.e. when input has been changed).
    */
    useEffect(() => {
        if (debouncedInput) setShouldRequestSuggestion(true);
    }, [debouncedInput]);

    useEffect(() => {
        if (
            shouldRequestSuggestion &&
            requestAutoCompleteSuggestions &&
            debouncedInput &&
            !toolboxInput &&
            !isSendDisabled &&
            /[A-Za-z]/.exec(debouncedInput)
        ) {
            requestAutoCompleteSuggestions(debouncedInput);
            setShouldRequestSuggestion(false);
        }
    }, [debouncedInput, requestAutoCompleteSuggestions, shouldRequestSuggestion, toolboxInput, isSendDisabled]);

    // Schedule a periodic request every 30 seconds for "Proactive suggestions" feature
    // This is separate from the regular auto-complete suggestions feature in the message input
    useEffect(() => {
        if (!requestAutoCompleteSuggestions) return;

        const interval = setInterval(() => {
            if (!useTimelineStore.getState().getTimelineState(currentSessionId).showAutocompleteSuggestions) {
                requestAutoCompleteSuggestions("Help ", true); // TODO: Can we remove the input value here?
                localLogger.info("AutoComplete | Proactive suggestion request");
            }
        }, proactiveSuggestionInterval);

        return () => clearInterval(interval);
        // oxlint-disable-next-line exhaustive-deps
    }, [proactiveSuggestionInterval, shouldRequestSuggestion, requestAutoCompleteSuggestions, currentSessionId]);

    /*
        This useEffect handles canceling the debounce.
        When handleSuggestionClick is called, input state value is
        not updated immediately within the scope of the function.
        So calling cancel there would happen too quickly
    */
    useEffect(() => {
        if (didClickSuggestion) {
            cancel();
            setDidClickSuggestion(false);
        }
    }, [didClickSuggestion, cancel]);

    return { cancel, handleSuggestionClick };
}
